import { useState } from 'react';
import styled from 'styled-components';

/**
 * OptimizedImage - Componente que usa Flyimg (https://images.cdn.beeapp.es)
 *
 * Flyimg automáticamente:
 * - Redimensiona imágenes según width/height
 * - Convierte a WebP/AVIF/JXL si el navegador lo soporta
 * - Optimiza calidad (quality param)
 * - Cachea las transformaciones
 * - Genera srcset automático para diferentes resoluciones
 *
 * @param {string} src - URL de la imagen original
 * @param {string} alt - Texto alternativo
 * @param {number} width - Ancho deseado en píxeles
 * @param {number} height - Alto deseado en píxeles (opcional)
 * @param {boolean} crop - Aplicar recorte cuando width y height están definidos (default: false)
 * @param {string} gravity - Posición del recorte: 'Center', 'North', 'South', 'East', 'West', 'NorthWest', 'NorthEast', 'SouthWest', 'SouthEast'
 * @param {number} quality - Calidad 1-100 (default: 90)
 * @param {string} output - Formato: 'auto', 'webp', 'avif', 'jxl', 'png', 'jpg' (default: 'auto')
 * @param {boolean} mozjpeg - Usar moz-jpeg compression (default: true)
 * @param {boolean} strip - Remover EXIF data (default: true)
 * @param {boolean} lazy - Lazy loading (default: true)
 * @param {boolean} responsive - Generar srcset automático (default: true)
 * @param {array} densities - Densidades para srcset (default: [1, 1.5, 2])
 * @param {string} className - Clase CSS adicional
 * @param {object} style - Estilos inline adicionales
 * @param {function} onClick - Handler de click
 * @param {string} placeholder - Color placeholder mientras carga (default: '#f0f0f0')
 * @param {string} filter - Algoritmo de resize: 'Lanczos', 'Triangle' (default: 'Lanczos')
 * @param {string} background - Color de fondo para padding (hex con %23, nombre CSS, o rgb)
 * @param {number} rotate - Rotación en grados
 * @param {boolean} faceCrop - Detectar y recortar cara (default: false)
 * @param {number} faceCropPosition - Índice de cara a recortar cuando hay múltiples (default: 0)
 * @param {boolean} faceBlur - Aplicar blur a caras detectadas (default: false)
 * @param {boolean} smartCrop - Usar recorte inteligente (default: false)
 * @param {string} unsharp - Sharpen usando unsharp mask (ej: '0x6' o '0.25x0.25+8+0.065')
 * @param {string} blur - Aplicar blur (ej: '2' o '1x2')
 * @param {string} text - Texto a añadir (watermark)
 * @param {string} textColor - Color del texto (default: 'white')
 * @param {number} textSize - Tamaño del texto (default: 12)
 * @param {string} textBackground - Color de fondo del texto
 * @param {boolean} webpLossless - WebP sin pérdida (default: false)
 */
const OptimizedImage = ({
  src,
  alt = '',
  width,
  height,
  crop = false,
  gravity = 'Center',
  quality = 90,
  output = 'auto',
  mozjpeg = true,
  strip = true,
  lazy = true,
  responsive = true,
  densities = [1, 1.5, 2], // 1x, 1.5x, 2x para pantallas Retina
  className = '',
  style = {},
  onClick,
  placeholder = '#f0f0f0',
  filter = 'Lanczos',
  background,
  rotate,
  faceCrop = false,
  faceCropPosition = 0,
  faceBlur = false,
  smartCrop = false,
  unsharp,
  blur,
  text,
  textColor = 'white',
  textSize = 12,
  textBackground,
  webpLossless = false,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Base URL de Flyimg
  const FLYIMG_BASE = 'https://images.cdn.beeapp.es/upload';

  /**
   * Construir parámetros de URL según la documentación de Flyimg
   * Formato: https://images.cdn.beeapp.es/upload/[params]/[image_url]
   */
  const buildFlyimgParams = (density = 1) => {
    const params = [];

    // Dimensiones
    if (width) params.push(`w_${Math.round(width * density)}`);
    if (height) params.push(`h_${Math.round(height * density)}`);

    // Crop y gravity
    if (crop) params.push('c_1');
    if (gravity && gravity !== 'Center') params.push(`g_${gravity}`);

    // Calidad y formato
    if (quality !== 90) params.push(`q_${quality}`);
    if (output !== 'auto') params.push(`o_${output}`);

    // Opciones de compresión
    if (!mozjpeg) params.push('moz_0');
    if (!strip) params.push('st_0');

    // WebP lossless
    if (webpLossless && (output === 'webp' || output === 'auto')) {
      params.push('webpl_1');
    }

    // Filtro de resize
    if (filter !== 'Lanczos') params.push(`f_${filter}`);

    // Background
    if (background) {
      // Convertir # a %23 para hex colors
      const bgParam = background.startsWith('#')
        ? `bg_${background.replace('#', '%23')}`
        : `bg_${background}`;
      params.push(bgParam);
    }

    // Rotación
    if (rotate) params.push(`r_${rotate}`);

    // Face detection
    if (faceCrop) {
      params.push('fc_1');
      if (faceCropPosition > 0) params.push(`fcp_${faceCropPosition}`);
    }
    if (faceBlur) params.push('fb_1');

    // Smart crop
    if (smartCrop) params.push('smc_1');

    // Sharpening y blur
    if (unsharp) params.push(`unsh_${unsharp}`);
    if (blur) params.push(`blr_${blur}`);

    // Texto (watermark)
    if (text) {
      params.push(`t_${encodeURIComponent(text)}`);
      if (textColor !== 'white') {
        const tcParam = textColor.startsWith('#')
          ? `tc_${textColor.replace('#', '%23')}`
          : `tc_${textColor}`;
        params.push(tcParam);
      }
      if (textSize !== 12) params.push(`ts_${textSize}`);
      if (textBackground) {
        const tbgParam = textBackground.startsWith('#')
          ? `tbg_${textBackground.replace('#', '%23')}`
          : `tbg_${textBackground}`;
        params.push(tbgParam);
      }
    }

    return params.join(',');
  };

  /**
   * Construir URL optimizada con Flyimg
   */
  const getOptimizedUrl = (density = 1) => {
    if (!src) {
      console.error('OptimizedImage: src is required');
      return '';
    }

    const params = buildFlyimgParams(density);

    // Formato: https://images.cdn.beeapp.es/upload/params/image_url
    if (params) {
      return `${FLYIMG_BASE}/${params}/${src}`;
    }

    // Si no hay parámetros, usar la imagen original
    return `${FLYIMG_BASE}/${src}`;
  };

  /**
   * Generar srcset para diferentes densidades de pantalla (Retina, etc.)
   */
  const getSrcSet = () => {
    if (!responsive || !width) {
      return undefined;
    }

    return densities
      .map(density => `${getOptimizedUrl(density)} ${density}x`)
      .join(', ');
  };

  const optimizedUrl = getOptimizedUrl();
  const srcSet = getSrcSet();

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleError = () => {
    setError(true);
    console.error('OptimizedImage: Failed to load image:', optimizedUrl);
  };

  if (error) {
    return (
      <ImagePlaceholder
        width={width}
        height={height}
        style={style}
        className={className}
      >
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorText>Error al cargar imagen</ErrorText>
      </ImagePlaceholder>
    );
  }

  return (
    <ImageWrapper style={style} className={className}>
      {!loaded && (
        <Skeleton
          width={width}
          height={height}
          placeholder={placeholder}
        />
      )}
      <StyledImage
        src={optimizedUrl}
        srcSet={srcSet}
        alt={alt}
        width={width}
        height={height}
        loading={lazy ? 'lazy' : 'eager'}
        onLoad={handleLoad}
        onError={handleError}
        onClick={onClick}
        $loaded={loaded}
        {...props}
      />
    </ImageWrapper>
  );
};

// Styled Components
const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  overflow: hidden;
`;

const StyledImage = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  opacity: ${props => props.$loaded ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const Skeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => props.width ? `${props.width}px` : '100%'};
  height: ${props => props.height ? `${props.height}px` : '200px'};
  background: linear-gradient(
    90deg,
    ${props => props.placeholder} 25%,
    #e0e0e0 50%,
    ${props => props.placeholder} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ImagePlaceholder = styled.div`
  width: ${props => props.width ? `${props.width}px` : '100%'};
  height: ${props => props.height ? `${props.height}px` : '200px'};
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #ccc;
`;

const ErrorIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #666;
`;

export default OptimizedImage;
